import React, { forwardRef, type CSSProperties, type ReactNode } from 'react';
import type { ExecutiveSummaryModel, ExecutiveVPRow } from '../../lib/operational-control/executive-summary-model';
import {
  EXECUTIVE_STATE_PRESENTATION,
  classifyMetricException,
  classifyPerformance,
  type ExecutiveSemanticState
} from '../../lib/operational-control/executive-summary-semantics';
import { EXECUTIVE_PDF_LAYOUT } from '../../lib/operational-control/executive-pdf-layout';
import type { OperationalControlSettings } from '../../lib/operational-control/operational-control-settings';

export type ExecutivePdfFinding = {
  title: string;
  state: ExecutiveSemanticState;
  badge: string;
};

type Props = {
  model: ExecutiveSummaryModel;
  settings: OperationalControlSettings;
  periodLabel: string;
  scope: string;
  generatedAt: string;
  version: string;
  priorities: ExecutivePdfFinding[];
  advances: ExecutivePdfFinding[];
  exceptions: ExecutivePdfFinding[];
};

const COLORS = {
  navy: '#102A43',
  navySoft: '#243B53',
  text: '#243B53',
  muted: '#52667A',
  line: '#CBD5E1',
  lineSoft: '#E2E8F0',
  white: '#FFFFFF',
  page: '#F7F8FA',
  stripe: '#F1F5F9'
};

const STATE_COLORS: Record<ExecutiveSemanticState, { text: string; background: string; border: string; marker: string }> = {
  OPTIMAL: { text: '#047857', background: '#ECFDF5', border: '#6EE7B7', marker: '#10B981' },
  STABLE: { text: '#854D0E', background: '#FEFCE8', border: '#FDE047', marker: '#EAB308' },
  ATTENTION: { text: '#C2410C', background: '#FFF7ED', border: '#FDBA74', marker: '#F97316' },
  CRITICAL: { text: '#B91C1C', background: '#FEF2F2', border: '#FCA5A5', marker: '#DC2626' },
  NEUTRAL: { text: '#475569', background: '#F1F5F9', border: '#CBD5E1', marker: '#64748B' },
  STARTUP: { text: '#0369A1', background: '#F0F9FF', border: '#7DD3FC', marker: '#0EA5E9' },
  STALE: { text: '#B45309', background: '#FFFBEB', border: '#FCD34D', marker: '#D97706' },
  NOT_CONFIGURED: { text: '#B45309', background: '#FFFBEB', border: '#FCD34D', marker: '#D97706' },
  NO_OBLIGATION: { text: '#52525B', background: '#F4F4F5', border: '#D4D4D8', marker: '#71717A' },
  NOT_APPLICABLE: { text: '#52525B', background: '#F4F4F5', border: '#D4D4D8', marker: '#71717A' },
  NO_CAPTURE: { text: '#B91C1C', background: '#FEF2F2', border: '#FCA5A5', marker: '#DC2626' },
  NO_PLAN: { text: '#52525B', background: '#F4F4F5', border: '#D4D4D8', marker: '#71717A' },
  NOT_STARTED: { text: '#0369A1', background: '#F0F9FF', border: '#7DD3FC', marker: '#0EA5E9' },
  TECHNICAL_ERROR: { text: '#B91C1C', background: '#FEF2F2', border: '#FCA5A5', marker: '#DC2626' }
};

const pct = (value: number | null, state?: ExecutiveSemanticState) => value === null ? (state ? EXECUTIVE_STATE_PRESENTATION[state].label.toUpperCase() : 'N/A') : `${Math.round(value)}%`;
const criticalAlertLabel = (count: number) => count === 0 ? '—' : `${count} ${count === 1 ? 'crítica' : 'críticas'}`;

const PdfCenteredZone = ({
  kind,
  height,
  lineHeight,
  children,
  style
}: {
  kind: string;
  height: number;
  lineHeight: number;
  children: ReactNode;
  style?: CSSProperties;
}) => (
  <div
    data-pdf-align-container
    data-pdf-align-kind={kind}
    style={{
      width: '100%',
      height,
      minHeight: height,
      flex: `0 0 ${height}px`,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
      padding: 0,
      textAlign: 'center',
      lineHeight: `${lineHeight}px`,
      ...style
    }}
  >
    <span data-pdf-align-text style={{ display: 'block', margin: 0, padding: 0, lineHeight: `${lineHeight}px`, textAlign: 'center', whiteSpace: 'nowrap' }}>{children}</span>
  </div>
);

const PdfBadge = ({ state, label }: { state: ExecutiveSemanticState; label?: string }) => {
  const colors = STATE_COLORS[state];
  return (
    <span data-pdf-badge style={{ height: 20, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, gap: 4, border: `1px solid ${colors.border}`, borderRadius: 10, background: colors.background, color: colors.text, padding: '0 6px', fontSize: 10.67, fontWeight: 700, lineHeight: '14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'block', width: 6, height: 6, borderRadius: '50%', background: colors.marker, flex: '0 0 6px' }} />
      <span style={{ display: 'block', lineHeight: '14px', margin: 0, padding: 0 }}>{label ?? EXECUTIVE_STATE_PRESENTATION[state].label}</span>
    </span>
  );
};

const PdfMetric = ({ value, state }: { value: number | null; state: ExecutiveSemanticState }) => {
  const colors = STATE_COLORS[state];
  if (value === null) {
    return <PdfBadge state={state} />;
  }
  return (
    <div data-pdf-numeric-metric style={{ minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: colors.text, fontSize: 12, fontWeight: 800, lineHeight: '16px', textAlign: 'center' }}><span style={{ color: colors.marker, fontSize: 10 }}>●</span>{pct(value)}</div>
  );
};

const PdfIndicatorMetric = ({ vp, state }: { vp: ExecutiveVPRow; state: ExecutiveSemanticState }) => {
  const value = vp.indicatorDisplay?.closedValue ?? null;
  const colors = STATE_COLORS[state];
  if (value === null) {
    const unconfigured = /sin indicadores configurados|sin configuraci[oó]n/i.test(vp.indicatorDisplay?.monthlyReason || '');
    return unconfigured
      ? <PdfBadge state="NOT_CONFIGURED" label="Sin configurar" />
      : <span style={{ color: STATE_COLORS.NOT_APPLICABLE.text, fontWeight: 700 }}>—</span>;
  }
  return <span data-pdf-numeric-metric title={`Acumulado cerrado ${vp.indicatorDisplay?.closedPeriod || ''}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: colors.text, fontSize: 12, fontWeight: 800, lineHeight: '16px', whiteSpace: 'nowrap' }}><span style={{ color: colors.marker, fontSize: 10 }}>●</span>{pct(value)}</span>;
};

const PdfAlertCount = ({ count }: { count: number }) => {
  const hasCriticalAlerts = count > 0;
  const colors = STATE_COLORS[hasCriticalAlerts ? 'CRITICAL' : 'NEUTRAL'];
  return (
    <div data-pdf-alert-count style={{ width: '100%', minHeight: 32, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: colors.text, fontSize: 11, fontWeight: 800, lineHeight: '14px', whiteSpace: 'nowrap' }}>
        {criticalAlertLabel(count)}
      </span>
    </div>
  );
};

const SummaryCard = ({ label, value, detail, state }: { label: string; value: string; detail?: string; state: ExecutiveSemanticState }) => {
  const colors = STATE_COLORS[state];
  return (
    <section data-pdf-summary-card style={{ position: 'relative', minWidth: 0, height: 88, border: `1px solid ${colors.border}`, borderTop: `4px solid ${colors.marker}`, borderRadius: 8, background: COLORS.white, boxSizing: 'border-box', textAlign: 'center' }}>
      <PdfCenteredZone kind="card-title" height={15} lineHeight={13} style={{ position: 'absolute', top: 7, left: 0, color: COLORS.muted, fontSize: 10.67, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase' }}>{label}</PdfCenteredZone>
      <PdfCenteredZone kind="card-value" height={24} lineHeight={23} style={{ position: 'absolute', top: 30, left: 0, color: colors.text, fontSize: 22, fontWeight: 900 }}>{value}</PdfCenteredZone>
      <PdfCenteredZone kind="card-detail" height={detail ? 15 : 0} lineHeight={13} style={{ position: 'absolute', bottom: 7, left: 0, display: detail ? 'flex' : 'none', color: colors.text, fontSize: 10.67, fontWeight: 700 }}>{detail ?? ''}</PdfCenteredZone>
    </section>
  );
};

const PdfTableHeading = ({ width, children }: { width: string; children: ReactNode }) => (
  <th style={{ width, height: 32, boxSizing: 'border-box', padding: '6px 8px', color: COLORS.white, fontSize: 10.67, lineHeight: '14px', verticalAlign: 'middle', textAlign: 'center' }}>
    <PdfCenteredZone kind="table-heading" height={20} lineHeight={14}>{children}</PdfCenteredZone>
  </th>
);

const tableCell: React.CSSProperties = {
  borderBottom: `1px solid ${COLORS.lineSoft}`,
  padding: '5px 8px',
  color: COLORS.text,
  fontSize: 11,
  lineHeight: 1.18,
  verticalAlign: 'middle',
  whiteSpace: 'normal',
  overflowWrap: 'break-word'
};

const PdfCellContent = ({ children }: { children: ReactNode }) => (
  <div style={{ width: '100%', minHeight: 24, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>{children}</div>
);

const PdfVpRow = ({ vp, settings, index, total }: { vp: ExecutiveVPRow; settings: OperationalControlSettings; index: number; total: number }) => {
  const planState = classifyMetricException(vp.planProgress, vp.status, 'plan', settings);
  const performanceState = classifyMetricException(vp.weightedPerformance, vp.status, 'performance', settings);
  const captureState = classifyMetricException(vp.capturePct, vp.captureApplicableCount ? vp.status : 'SIN_OBLIGACION', 'capture', settings);
  return (
    <tr data-pdf-vp-row data-pdf-first-row={index === 0 ? 'true' : undefined} data-pdf-last-row={index === total - 1 ? 'true' : undefined} style={{ background: index % 2 === 0 ? COLORS.white : COLORS.stripe }}>
      <td style={{ ...tableCell, width: '27%' }}><strong style={{ display: 'block', color: COLORS.navy, fontSize: 11.5, lineHeight: '14px' }}>{vp.vpName}</strong><span style={{ display: 'block', marginTop: 2, color: COLORS.muted, fontSize: 10.67, lineHeight: '13px' }}>{vp.presidentName}</span></td>
      <td style={{ ...tableCell, width: '17%' }}><strong style={{ display: 'block', color: COLORS.navy, lineHeight: '14px' }}>{vp.instanceCounts.total} total</strong><div style={{ marginTop: 2, color: COLORS.muted, fontSize: 10.67, lineHeight: '13px' }}>{vp.instanceCounts.updated} act. · {vp.instanceCounts.stale} rez. · {vp.instanceCounts.exception} exc.</div></td>
      <td style={{ ...tableCell, width: '10%', textAlign: 'center' }}><PdfCellContent><PdfMetric value={vp.planProgress} state={planState} /></PdfCellContent></td>
      <td data-pdf-indicator-evaluation style={{ ...tableCell, width: '11%', textAlign: 'center', whiteSpace: 'nowrap' }}><PdfCellContent><PdfIndicatorMetric vp={vp} state={classifyMetricException(vp.indicatorDisplay?.closedValue ?? null, vp.status, 'indicators', settings)} /></PdfCellContent></td>
      <td style={{ ...tableCell, width: '11%', textAlign: 'center' }}><PdfCellContent><PdfMetric value={vp.weightedPerformance} state={performanceState} /></PdfCellContent></td>
      <td style={{ ...tableCell, width: '10%', textAlign: 'center' }}><PdfCellContent><PdfMetric value={vp.capturePct} state={captureState} /></PdfCellContent></td>
      <td style={{ ...tableCell, width: '14%', textAlign: 'center' }}><PdfCellContent><PdfAlertCount count={vp.criticalCount} /></PdfCellContent></td>
    </tr>
  );
};

const FindingGroup = ({ title, rows, empty }: { title: string; rows: ExecutivePdfFinding[]; empty: string }) => (
  <section data-pdf-finding-group style={{ border: `1px solid ${COLORS.line}`, borderRadius: 8, background: COLORS.white, padding: '8px 7px', minWidth: 0 }}>
    <PdfCenteredZone kind="finding-heading" height={16} lineHeight={14} style={{ color: COLORS.navy, fontSize: 10.67, fontWeight: 900, letterSpacing: '.05em', textTransform: 'uppercase' }}>{title}</PdfCenteredZone>
    <div style={{ marginTop: 5, display: 'grid', gap: 4 }}>
      {rows.slice(0, 3).map((row, index) => <div key={`${row.title}-${index}`} style={{ minHeight: 20, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) max-content', alignItems: 'center', columnGap: 6, borderTop: index === 0 ? 'none' : `1px solid ${COLORS.lineSoft}`, paddingTop: index === 0 ? 0 : 4 }}><span data-pdf-finding-title style={{ minWidth: 0, color: COLORS.text, fontSize: 10.67, fontWeight: 700, lineHeight: '13px' }}>{row.title}</span><PdfBadge state={row.state} label={row.badge} /></div>)}
      {rows.length === 0 && <div style={{ minHeight: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.muted, fontSize: 10.67, lineHeight: '13px', textAlign: 'center' }}>{empty}</div>}
    </div>
  </section>
);

export const ExecutiveOperationalSummaryPdf = forwardRef<HTMLDivElement, Props>(({
  model,
  settings,
  periodLabel,
  scope,
  generatedAt,
  priorities,
  advances,
  exceptions
}, ref) => {
  const generatedDate = generatedAt.split(',')[0]?.trim() || generatedAt.trim();
  const healthState = classifyPerformance(model.national.healthScore, settings);
  const planState = classifyPerformance(model.national.planProgress, settings);
  const indicatorState = classifyPerformance(model.national.kpiCompliance, settings);
  const captureState = classifyPerformance(model.national.capturePct, settings);
  const alertState = model.national.criticalInstances > 0 ? 'CRITICAL' : 'NEUTRAL';
  const exceptionState = model.national.noConfigurationInstances > 0 ? 'NOT_CONFIGURED' : model.national.staleInstances > 0 ? 'STALE' : 'OPTIMAL';

  return (
    <article ref={ref} data-pdf-export-surface data-pdf-format="A3" data-pdf-orientation="landscape" style={{ width: EXECUTIVE_PDF_LAYOUT.surfaceWidthPx, minHeight: 1036, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 8, background: COLORS.page, color: COLORS.text, padding: '28px 34px 22px', fontFamily: 'Arial, sans-serif', fontVariantNumeric: 'tabular-nums', textRendering: 'geometricPrecision' }}>
      <header data-pdf-header style={{ minHeight: 76, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 28, borderBottom: `3px solid ${COLORS.navy}`, paddingBottom: 10, boxSizing: 'border-box' }}>
        <div data-pdf-header-title style={{ flex: '1 1 auto', minWidth: 0 }}><div style={{ color: '#0369A1', fontSize: 11, fontWeight: 900, lineHeight: '14px', letterSpacing: '.12em', textTransform: 'uppercase' }}>Gobernanza Institucional</div><h1 style={{ margin: '3px 0 0', color: COLORS.navy, fontSize: 25, lineHeight: '28px', fontWeight: 900, textTransform: 'uppercase' }}>Control Operativo · Resumen Ejecutivo</h1><div style={{ marginTop: 4, color: COLORS.navySoft, fontSize: 11.5, fontWeight: 700, lineHeight: '14px' }}>{periodLabel} · {scope}</div></div>
        <div data-pdf-header-meta style={{ flex: '0 0 350px', width: 350, minWidth: 350, display: 'grid', gridTemplateRows: 'repeat(2, 14px)', rowGap: 2, alignContent: 'center', justifyItems: 'end', color: COLORS.muted, fontSize: 10.67, lineHeight: '14px', textAlign: 'right', whiteSpace: 'nowrap' }}><span>Generado: {generatedDate}</span><span>Desarrollado por IAPRIORI</span></div>
      </header>

      <section data-pdf-national-summary style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0, 1fr))', gap: 7 }}>
        <SummaryCard label="Salud" value={pct(model.national.healthScore, healthState)} state={healthState} detail={model.national.healthScore === null ? '' : undefined} />
        <SummaryCard label="Vicepresidencias" value={String(model.national.visibleVicepresidencies)} state="NEUTRAL" detail="13 VP + Secretaría General" />
        <SummaryCard label="Instancias" value={String(model.national.visibleInstances)} state="NEUTRAL" detail="Alcance ejecutivo" />
        <SummaryCard label="Plan" value={pct(model.national.planProgress, planState)} state={planState} detail={model.national.planProgress === null ? '' : undefined} />
        <SummaryCard label="Indicadores" value={pct(model.national.kpiCompliance, indicatorState)} state={indicatorState} detail={model.national.kpiCompliance === null ? '' : undefined} />
        <SummaryCard label="Captura" value={pct(model.national.capturePct, captureState)} state={captureState} detail={model.national.capturePct === null ? '' : undefined} />
        <SummaryCard label="Alertas" value={criticalAlertLabel(model.national.criticalInstances)} state={alertState} detail="" />
        <SummaryCard label="Excepciones" value={String(model.national.noConfigurationInstances + model.national.onboardingInstances + model.national.staleInstances)} state={exceptionState} detail={`${model.national.staleInstances} rez. · ${model.national.onboardingInstances} arr. · ${model.national.noConfigurationInstances} config.`} />
      </section>

      <section data-pdf-main-table style={{ border: `1px solid ${COLORS.line}`, borderRadius: 8, background: COLORS.white }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead><tr style={{ height: 32, background: COLORS.navy }}><PdfTableHeading width="27%">Vicepresidencia / titular</PdfTableHeading><PdfTableHeading width="17%">Instancias</PdfTableHeading><PdfTableHeading width="10%">Plan</PdfTableHeading><PdfTableHeading width="11%">Indicadores</PdfTableHeading><PdfTableHeading width="11%">Desempeño</PdfTableHeading><PdfTableHeading width="10%">Captura</PdfTableHeading><PdfTableHeading width="14%">Alertas</PdfTableHeading></tr></thead>
          <tbody>{model.vicePresidencies.map((vp, index) => <PdfVpRow key={vp.vpId || vp.vpName} vp={vp} settings={settings} index={index} total={model.vicePresidencies.length} />)}</tbody>
        </table>
      </section>

      <section data-pdf-executive-strip style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}><FindingGroup title="Prioridades" rows={priorities} empty="Sin prioridades críticas" /><FindingGroup title="Mejores desempeños" rows={advances} empty="Sin desempeño aplicable" /><FindingGroup title="Excepciones relevantes" rows={exceptions} empty="Sin excepciones" /></section>

      <footer data-pdf-footer style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, borderTop: `1px solid ${COLORS.line}`, paddingTop: 6, color: COLORS.muted, fontSize: 10.67 }}><span><span data-pdf-legend style={{ fontSize: 9.5 }}>Captura: Sin obligación = sin meta aplicable · Sin captura = obligación sin evidencia. Indicadores: — = sin acumulado válido · 0% = cero válido.</span><br />Integral VP: contrato canónico de indicadores cerrados y ponderación por instancia. Mensual informativo; denominadores separados.</span><strong style={{ color: COLORS.navy, whiteSpace: 'nowrap' }}>Página 1 de 1</strong></footer>
    </article>
  );
});

ExecutiveOperationalSummaryPdf.displayName = 'ExecutiveOperationalSummaryPdf';
